import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code, Terminal, X, Menu } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../ui/button';

const Header = () => {
  const { personalInfo } = useAppContext();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Convert section ID to lowercase to match the actual HTML IDs
    const section = document.getElementById(sectionId.toLowerCase());
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/90 backdrop-blur-md border-b border-border/50' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <motion.div
            className="flex items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Terminal className="h-6 w-6 text-primary mr-2" />
            <span className="text-xl font-bold terminal-text">
              <span className="text-primary">{'>'}</span> {personalInfo.name.split(' ')[0]}
              <span className="text-primary">_</span>
            </span>
          </motion.div>

          <nav className="hidden md:flex items-center space-x-6">
            {['About', 'Education', 'Experience', 'Projects', 'Contact'].map((section, index) => (
              <motion.button
                key={section}
                className="text-foreground/80 hover:text-primary transition-colors terminal-text animated-underline"
                onClick={() => scrollToSection(section)}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
              >
                <span className="text-primary">/</span>
                {section}
              </motion.button>
            ))}

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
            >
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                onClick={() => window.open(personalInfo.resumePath, '_blank')}
              >
                <Code className="mr-2 h-4 w-4" />
                Resume
              </Button>
            </motion.div>
          </nav>

          <motion.div
            className="md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </motion.div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <motion.div
          className="md:hidden bg-background/95 backdrop-blur-md border-b border-border"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <div className="container mx-auto px-4 py-4">
            <nav className="flex flex-col space-y-4">
              {['about', 'education', 'experience', 'projects', 'contact'].map((section) => (
                <button
                  key={section}
                  className="text-foreground/80 hover:text-primary transition-colors py-2 terminal-text text-left"
                  onClick={() => scrollToSection(section)}
                >
                  <span className="text-primary">/</span>
                  {section}
                </button>
              ))}

              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10 w-full justify-start"
                onClick={() => window.open(personalInfo.resumePath, '_blank')}
              >
                <Code className="mr-2 h-4 w-4" />
                Resume
              </Button>
            </nav>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;
